import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search,
  Megaphone,
  Shield,
  CheckCircle2,
  Clock,
  ExternalLink,
  X,
  RefreshCw,
  Eye,
  Check,
  Calendar,
  AlertTriangle,
  Mail,
  Loader2,
  Building2,
  Landmark,
  Globe,
  User as UserIcon,
  Users,
  GraduationCap,
  Sparkles,
  Maximize2,
  FileText,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { toast } from 'sonner';
import { listPendingApprovals } from '../../security/deviceApprovalApi';
import { registerAuthenticatedPushToken } from '../../security/pushApi';
import { ComunicadoVisualizacaoModal } from '../../components/comunicados/ComunicadoVisualizacaoModal';
import { MobileTabBar } from '../professor/MobileTabBar';
import {
  getUserDevices,
  revokeUserDevice,
  getInactiveDevices,
  formatRelativeDeviceAccess,
  type Dispositivo,
} from '../../utils/userDevices';

const API = import.meta.env.VITE_API_URL || '/api/v1';

export interface AlertaPessoalItem {
  id: string;
  origem: 'comunicado' | 'seguranca' | 'sistema';
  titulo: string;
  mensagem: string;
  tipo?: string;
  categoria?: string;
  destinatario?: string;
  criadoEm: string;
  lido: boolean;
  meta?: any;
  rotaDestino?: string;
  isActionItem?: boolean;
  actionType?: 'email_verification' | 'push_activation' | 'device_disconnect';
}

/** Formata tempo relativo em português amigável */
function formatRelativeTime(dateInput: string | Date | undefined): string {
  if (!dateInput) return '';
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return '';
  const now = Date.now();
  const diffSec = Math.floor((now - d.getTime()) / 1000);
  if (diffSec < 45) return 'agora há pouco';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `há ${diffMin} min`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `há ${diffHour}h`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay === 1) return 'ontem';
  if (diffDay < 7) return `há ${diffDay} dias`;
  if (diffDay < 30) return `há ${Math.floor(diffDay / 7)} sem`;
  return `há ${Math.floor(diffDay / 30)} meses`;
}

/** Formata data e hora exatas */
function formatDateTime(dateInput: string | Date | undefined): string {
  if (!dateInput) return '';
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Extrai URL de mídia/imagem anexada do comunicado */
function getMidiaUrl(item: any): string | null {
  if (!item) return null;
  const meta = item.meta || item;
  const cfg = meta.configPreview;
  const url =
    cfg?.midiaUrl ||
    cfg?.arquivoUrl ||
    cfg?.bannerUrl ||
    meta.midiaUrl ||
    meta.imagemUrl ||
    meta.anexoUrl ||
    meta.arquivoUrl ||
    meta.fotoUrl;

  if (typeof url === 'string' && url.trim().length > 4) {
    return url.trim();
  }
  return null;
}

/** Identifica o emissor/remetente (Município, Escola, Estado, Autor) */
function resolveSenderInfo(item: any) {
  const meta = item.meta || item;
  const autor = meta.criadoPor?.nome || meta.autorNome;
  const perfilAutor = meta.criadoPor?.perfil;
  const escola = meta.escola?.nome || meta.escolaNome;
  const municipio = meta.municipio?.nome || meta.municipioNome;
  const estado = meta.estado?.nome || meta.estadoSigla;

  if (municipio) {
    return {
      label: `Município de ${municipio}`,
      sub: autor ? `Enviado por ${autor}` : 'Secretaria Municipal de Educação',
      icon: Landmark,
      color: '#0284c7',
      bg: '#e0f2fe',
    };
  }

  if (escola) {
    return {
      label: escola,
      sub: autor ? `Enviado por ${autor}` : 'Diretoria / Coordenação Escolar',
      icon: Building2,
      color: '#7c3aed',
      bg: '#f3e8ff',
    };
  }

  if (estado) {
    return {
      label: `Governo Estadual / Rede (${estado})`,
      sub: autor ? `Enviado por ${autor}` : 'Secretaria Estadual de Educação',
      icon: Globe,
      color: '#059669',
      bg: '#d1fae5',
    };
  }

  if (perfilAutor === 'ADMIN' || meta.origem === 'sistema' || !escola) {
    return {
      label: 'Administração Central',
      sub: autor ? `Enviado por ${autor}` : 'Comunicado do Sistema',
      icon: Landmark,
      color: '#475569',
      bg: '#f1f5f9',
    };
  }

  return {
    label: autor || 'Coordenação Escolar',
    sub: 'Comunicado Oficial',
    icon: UserIcon,
    color: '#475569',
    bg: '#f1f5f9',
  };
}

/** Identifica o público-alvo e destaca se for especificamente para o perfil logado */
function resolveTargetInfo(item: any, userPerfil?: string) {
  const meta = item.meta || item;
  const dest = (meta.destinatario || meta.publicoAlvo || meta.alvo || '').toUpperCase();
  const turma = meta.turma?.nome || meta.turmaNome;

  let label = 'Todos (Geral)';
  let isDirect = false;
  let color = '#475569';
  let bg = '#f1f5f9';
  let icon = Users;

  if (dest.includes('PROFESSOR')) {
    label = 'Professores';
    color = '#0284c7';
    bg = '#e0f2fe';
    icon = GraduationCap;
    if (userPerfil === 'PROFESSOR') isDirect = true;
  } else if (dest.includes('RESPONSAVEL') || dest.includes('PAIS')) {
    label = 'Responsáveis';
    color = '#d97706';
    bg = '#fef3c7';
    icon = Users;
    if (userPerfil === 'RESPONSAVEL') isDirect = true;
  } else if (dest.includes('ALUNO')) {
    label = 'Alunos';
    color = '#7c3aed';
    bg = '#f3e8ff';
    icon = Users;
    if (userPerfil === 'ALUNO') isDirect = true;
  } else if (
    dest.includes('EQUIPE') ||
    dest.includes('DIRECAO') ||
    dest.includes('COORDENACAO') ||
    dest.includes('SECRETARIA')
  ) {
    label = 'Equipe Escolar';
    color = '#059669';
    bg = '#d1fae5';
    icon = Building2;
    if (['ESCOLA', 'ADMIN', 'EQUIPE', 'MUNICIPAL'].includes(userPerfil || '')) isDirect = true;
  } else if (dest.includes('MUNICIPAL')) {
    label = 'Gestão Municipal';
    color = '#0284c7';
    bg = '#e0f2fe';
    icon = Landmark;
    if (userPerfil === 'MUNICIPAL') isDirect = true;
  }

  if (turma) {
    label += ` · ${turma}`;
  }

  return { label, isDirect, color, bg, icon };
}

export function MeusComunicadosPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, checkAuth } = useAuthStore();

  const [activeTab, setActiveTab] = useState<'todos' | 'comunicados' | 'seguranca'>('todos');
  const [filterStatus, setFilterStatus] = useState<'todos' | 'nao_lidos' | 'lidos'>('nao_lidos');
  const [hasInitializedFilter, setHasInitializedFilter] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Diálogo de Visualização Oficial do Comunicado
  const [selectedComunicado, setSelectedComunicado] = useState<any | null>(null);

  // Modal de Imagem Fullscreen (Lightbox)
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  // Push notifications state
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission>(() => {
    return typeof Notification !== 'undefined' ? Notification.permission : 'default';
  });
  const [notifLoading, setNotifLoading] = useState(false);
  const isPushNotActive =
    permissionStatus !== 'granted' &&
    !user?.fcmToken &&
    typeof window !== 'undefined' &&
    localStorage.getItem('ac_push_active') !== 'true';

  // Estados locais de dados
  const [comunicados, setComunicados] = useState<any[]>([]);
  const [approvals, setApprovals] = useState<any[]>([]);
  const [readComunicadoIds, setReadComunicadoIds] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('ac_read_comunicados') || '[]');
    } catch {
      return [];
    }
  });
  const [openedApprovalIds, setOpenedApprovalIds] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('ac_opened_approvals') || '[]');
    } catch {
      return [];
    }
  });

  // Sincronização de Dispositivos e Alertas de Inatividade (+30 dias)
  const [devicesList, setDevicesList] = useState<Dispositivo[]>(getUserDevices);
  useEffect(() => {
    const handleDevSync = () => setDevicesList(getUserDevices());
    window.addEventListener('ac:devices:updated', handleDevSync);
    return () => window.removeEventListener('ac:devices:updated', handleDevSync);
  }, []);
  const inactiveDevices = useMemo(() => getInactiveDevices(devicesList), [devicesList]);

  const handleActivatePush = async () => {
    setNotifLoading(true);
    try {
      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);
      if (permission !== 'granted') {
        toast.error('Permissão de notificações não concedida no navegador.');
        return;
      }
      const { initializeApp, getApps } = await import('firebase/app');
      const { getMessaging, getToken, onMessage } = await import('firebase/messaging');
      const cfg = {
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
        appId: import.meta.env.VITE_FIREBASE_APP_ID,
      };
      const app = getApps().length ? getApps()[0] : initializeApp(cfg);
      const messaging = getMessaging(app);
      const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY as string;
      const fcmToken = await getToken(messaging, { vapidKey });
      onMessage(messaging, (payload) => {
        if (payload.notification) {
          new Notification(payload.notification.title || 'Aluno Conectado', {
            body: payload.notification.body || '',
            icon: '/icons/icon-192x192.png',
          });
        }
      });
      const jwtToken = localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token') || '';
      await registerAuthenticatedPushToken(
        { fcmToken, plataforma: 'WEB', tipo: 'desktop' },
        { bearerToken: jwtToken }
      );
      localStorage.setItem('ac_push_active', 'true');
      toast.success('Notificações push ativadas com sucesso!');
      loadData();
    } catch (err) {
      console.error('[MeusComunicadosPage] Erro ao ativar push:', err);
      toast.error('Não foi possível ativar as notificações push.');
    } finally {
      setNotifLoading(false);
    }
  };

  useEffect(() => {
    const handleVerified = () => loadData();
    window.addEventListener('ac:email:verified', handleVerified);
    return () => window.removeEventListener('ac:email:verified', handleVerified);
  }, []);

  const loadData = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      let endpoint = '';
      if (user?.escolaId) {
        endpoint = `/escolas/${user.escolaId}/comunicados`;
      } else if (user?.municipioId) {
        endpoint = `/municipios/${user.municipioId}/comunicados`;
      } else if (user?.perfil === 'ADMIN') {
        endpoint = '/master/comunicados';
      } else if (user?.perfil === 'RESPONSAVEL') {
        endpoint = '/mobile/responsavel/comunicados';
      } else if (user?.perfil === 'PROFESSOR') {
        endpoint = user?.escolaId ? `/escolas/${user.escolaId}/comunicados` : '';
      }

      if (endpoint) {
        const res = await api.get(endpoint);
        const lista = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
        if (lista.length > 0) {
          const filtered = lista.filter((c: any) => {
            // 1. Nunca recebe comunicados criados pelo próprio usuário logado
            if (c.criadoPorId && user?.id && c.criadoPorId === user.id) return false;
            if (c.criadoPor?.id && user?.id && c.criadoPor.id === user.id) return false;

            // 2. Se for MUNICIPAL, não recebe os comunicados municipais emitidos para a rede, a não ser que venham de instância superior (ESTADO, REDE, MASTER)
            if (user?.perfil === 'MUNICIPAL') {
              const isOrigemSuperior =
                c.tipoEmissor === 'ESTADO' ||
                c.tipoEmissor === 'REDE' ||
                c.tipoEmissor === 'ADMIN' ||
                c.origem === 'ESTADUAL' ||
                c.origem === 'REDE' ||
                c.origem === 'MASTER' ||
                c.origem === 'ADMIN' ||
                Boolean(c.origemMaster) ||
                Boolean(c.origemEstadual);

              if (!isOrigemSuperior) {
                return false;
              }
            }

            // 3. Se for ESCOLA, não recebe comunicados criados pela própria escola para seus alunos/pais
            if (user?.perfil === 'ESCOLA' && c.escolaId === user?.escolaId && c.criadoPorId === user?.id) {
              return false;
            }

            return true;
          });

          setComunicados(filtered);

          // Sincroniza IDs já lidos no servidor para eliminar discrepância entre dispositivos
          const serverReadIds = filtered
            .filter(
              (c: any) =>
                c.visualizado ||
                c.lido ||
                c.visualizadoEm ||
                (Array.isArray(c.visualizacoes) && c.visualizacoes.length > 0),
            )
            .map((c: any) => c.id);
          if (serverReadIds.length > 0) {
            setReadComunicadoIds((prev) =>
              Array.from(new Set([...prev, ...serverReadIds])),
            );
          }
        }
      }

      // Carrega aprovações de segurança
      const pend = await listPendingApprovals();
      setApprovals(Array.isArray(pend) ? pend : []);
    } catch (err) {
      console.warn('[MeusComunicadosPage] Erro ao carregar alertas:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    loadData();

    const handleSyncRead = (e: any) => {
      const id = e.detail?.id;
      const all = e.detail?.all;
      const ids = e.detail?.ids;
      if (all && ids) {
        setReadComunicadoIds((prev) => Array.from(new Set([...prev, ...ids])));
      } else if (id) {
        setReadComunicadoIds((prev) => Array.from(new Set([...prev, id])));
      }
    };
    window.addEventListener('ac:comunicado:read', handleSyncRead);
    return () => {
      window.removeEventListener('ac:comunicado:read', handleSyncRead);
    };
  }, [loadData]);

  // Deep-link na página: se houver ?comunicadoId=XYZ na URL, abre o comunicado automaticamente
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const targetId = params.get('comunicadoId') || params.get('notificacaoId');
    if (!targetId || loading) return;

    const found = comunicados.find((c) => c.id === targetId);
    if (found) {
      setSelectedComunicado(found);
      handleMarkAsRead(found.id, 'comunicado');
    } else {
      api.get(`/comunicados/${targetId}`).then((res) => {
        if (res.data) {
          setSelectedComunicado(res.data);
          handleMarkAsRead(res.data.id, 'comunicado');
        }
      }).catch(() => {});
    }

    params.delete('comunicadoId');
    params.delete('notificacaoId');
    const newSearch = params.toString();
    navigate(location.pathname + (newSearch ? `?${newSearch}` : ''), { replace: true });
  }, [location.search, comunicados, loading]);

  // Lista unificada normalizada (Comunicados + Segurança & Configurações)
  const unifiedList = useMemo(() => {
    const list: AlertaPessoalItem[] = [];

    // 1. Alerta Crítico de E-mail não verificado
    if (user && !user.emailVerificado) {
      list.push({
        id: 'sec-email-unverified',
        origem: 'seguranca',
        titulo: 'E-mail não verificado',
        mensagem:
          'Sua conta está vulnerável. Confirme seu e-mail para garantir a recuperação de senha e segurança de acesso.',
        tipo: 'SEGURANÇA',
        criadoEm: new Date().toISOString(),
        lido: false,
        isActionItem: true,
        actionType: 'email_verification',
      });
    }

    // 2. Alerta de Notificações Push desativadas
    if (isPushNotActive) {
      list.push({
        id: 'sec-push-inactive',
        origem: 'seguranca',
        titulo:
          permissionStatus === 'denied'
            ? 'Notificações Bloqueadas'
            : 'Ativar Notificações Push',
        mensagem:
          permissionStatus === 'denied'
            ? 'As notificações foram bloqueadas nas configurações do seu navegador. Libere a permissão no ícone de cadeado ao lado da URL.'
            : 'Ative as notificações push para receber alertas de logins e acessos importantes nesta máquina em tempo real.',
        tipo: 'CONFIGURAÇÃO',
        criadoEm: new Date().toISOString(),
        lido: false,
        isActionItem: true,
        actionType: 'push_activation',
      });
    }

    // 3. Comunicados recebidos
    comunicados.forEach((c) => {
      const isLido =
        readComunicadoIds.includes(c.id) ||
        Boolean(c.lido) ||
        Boolean(c.visualizado) ||
        Boolean(c.visualizadoEm) ||
        (Array.isArray(c.visualizacoes) && c.visualizacoes.length > 0);
      list.push({
        id: c.id,
        origem: 'comunicado',
        titulo: c.titulo || 'Comunicado Oficial',
        mensagem: c.corpo || '',
        tipo: c.tipo || 'AVISO',
        categoria: c.configPreview?.categoriaOriginal || c.categoria || 'OPERACIONAL',
        destinatario: c.destinatario || c.publicoAlvo || 'TODOS',
        criadoEm: c.criadoEm || new Date().toISOString(),
        lido: isLido,
        meta: c,
      });
    });

    // 4. Alertas de aprovação de segurança
    approvals.forEach((ap) => {
      const isExpired =
        ap.status === 'EXPIRADO' || (ap.expiraEm && new Date(ap.expiraEm).getTime() <= Date.now());
      const isOpened = openedApprovalIds.includes(ap.id);

      list.push({
        id: ap.id,
        origem: 'seguranca',
        titulo:
          ap.tipo === 'BIOMETRIA_DIARIA'
            ? (isExpired ? 'Biometria Expirada' : 'Confirmação Biométrica')
            : (isExpired ? 'Acesso Expirado' : 'Nova Solicitação de Acesso'),
        mensagem: ap.contexto?.navegador
          ? `Dispositivo: ${ap.contexto.navegador} (${ap.contexto.sistemaOp || 'OS'}) ${ap.contexto.ip ? `· IP: ${ap.contexto.ip}` : ''}`
          : 'Solicitação de segurança pendente na sua conta.',
        tipo: ap.tipo || 'SEGURANÇA',
        criadoEm: ap.criadoEm || new Date().toISOString(),
        lido: isOpened || isExpired,
        meta: ap,
        rotaDestino: isExpired ? undefined : `/security/approve/${ap.id}`,
      });
    });

    // 5. Alertas de Dispositivos Inativos (+30 dias)
    inactiveDevices.forEach((dev) => {
      const timeInfo = formatRelativeDeviceAccess(dev.ultimoAcesso, false);
      list.push({
        id: `sec-device-inactive-${dev.id}`,
        origem: 'seguranca',
        titulo: `Dispositivo Inativo (+30 dias): ${dev.nome || dev.navegadorDetectado}`,
        mensagem: `O aparelho "${dev.nome || dev.navegadorDetectado}" (${dev.sistemaOp || 'Sistema'}) não é acessado ${timeInfo.label.toLowerCase()}. Para manter sua conta segura, recomendamos desconectá-lo.`,
        tipo: 'SEGURANÇA',
        criadoEm: dev.ultimoAcesso || new Date().toISOString(),
        lido: false,
        isActionItem: true,
        actionType: 'device_disconnect',
        meta: dev,
      });
    });

    return list.sort(
      (a, b) => new Date(b.criadoEm).getTime() - new Date(a.criadoEm).getTime()
    );
  }, [comunicados, approvals, readComunicadoIds, openedApprovalIds, user, isPushNotActive, permissionStatus, inactiveDevices]);

  // Filtros aplicados
  const filteredList = useMemo(() => {
    return unifiedList.filter((item) => {
      if (activeTab === 'comunicados' && item.origem !== 'comunicado') return false;
      if (activeTab === 'seguranca' && item.origem !== 'seguranca') return false;

      if (filterStatus === 'nao_lidos' && item.lido) return false;
      if (filterStatus === 'lidos' && !item.lido) return false;

      if (search.trim()) {
        const q = search.toLowerCase();
        const matchTitle = item.titulo.toLowerCase().includes(q);
        const matchMsg = item.mensagem.toLowerCase().includes(q);
        const matchTipo = (item.tipo || '').toLowerCase().includes(q);
        const matchSender =
          (item.meta?.escola?.nome || '').toLowerCase().includes(q) ||
          (item.meta?.municipio?.nome || '').toLowerCase().includes(q) ||
          (item.meta?.criadoPor?.nome || '').toLowerCase().includes(q);
        if (!matchTitle && !matchMsg && !matchTipo && !matchSender) return false;
      }

      return true;
    });
  }, [unifiedList, activeTab, filterStatus, search]);

  const totalSeguranca = useMemo(() => {
    return unifiedList.filter((item) => item.origem === 'seguranca').length;
  }, [unifiedList]);

  const hasCriticalSecurityAlert = useMemo(() => {
    return unifiedList.some(
      (item) =>
        item.origem === 'seguranca' &&
        item.tipo !== 'CONFIGURAÇÃO' &&
        item.actionType !== 'push_activation' &&
        !item.lido
    );
  }, [unifiedList]);

  const securityColor = hasCriticalSecurityAlert ? '#dc2626' : '#d97706';

  const totalNaoLidos = useMemo(() => {
    return unifiedList.filter((item) => !item.lido).length;
  }, [unifiedList]);

  // Regra de entrada padrão: abre com 'Não Lidos' selecionado; se não houver itens não lidos, abre 'Todos'
  useEffect(() => {
    if (loading) return;
    if (!hasInitializedFilter) {
      if (totalNaoLidos > 0) {
        setFilterStatus('nao_lidos');
      } else {
        setFilterStatus('todos');
      }
      setHasInitializedFilter(true);
    }
  }, [loading, totalNaoLidos, hasInitializedFilter]);

  // Ações de leitura
  const handleMarkAsRead = (id: string, origem: 'comunicado' | 'seguranca' | 'sistema' = 'comunicado') => {
    if (origem === 'comunicado') {
      const updated = Array.from(new Set([...readComunicadoIds, id]));
      setReadComunicadoIds(updated);
      try {
        localStorage.setItem('ac_read_comunicados', JSON.stringify(updated));
      } catch {}
      // Notifica o sininho superior em tempo real
      window.dispatchEvent(new CustomEvent('ac:comunicado:read', { detail: { id } }));
      // Persiste a visualização/leitura no backend
      api.post(`/comunicados/${id}/visualizar`).catch(() => {});
    } else {
      const updated = Array.from(new Set([...openedApprovalIds, id]));
      setOpenedApprovalIds(updated);
      try {
        localStorage.setItem('ac_opened_approvals', JSON.stringify(updated));
      } catch {}
    }
  };

  const handleMarkAllAsRead = () => {
    const allComunicados = comunicados.map((c) => c.id);
    const updatedC = Array.from(new Set([...readComunicadoIds, ...allComunicados]));
    setReadComunicadoIds(updatedC);
    try {
      localStorage.setItem('ac_read_comunicados', JSON.stringify(updatedC));
    } catch {}

    // Notifica o BaseLayout / Navbar para zerar o badge e limpar o popover imediatamente
    window.dispatchEvent(
      new CustomEvent('ac:comunicado:read', { detail: { all: true, ids: allComunicados } }),
    );

    // Persiste a visualização em lote no backend
    if (allComunicados.length > 0) {
      api.post('/comunicados/visualizar-lote', { ids: allComunicados }).catch(() => {});
    }

    const allApprovals = approvals.map((a) => a.id);
    const updatedA = Array.from(new Set([...openedApprovalIds, ...allApprovals]));
    setOpenedApprovalIds(updatedA);
    try {
      localStorage.setItem('ac_opened_approvals', JSON.stringify(updatedA));
    } catch {}

    toast.success('Todos os comunicados foram marcados como lidos.');
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '16px 20px 40px' }}>
      {/* Header da Página */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
        marginBottom: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
          }}>
            <Megaphone size={22} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h1 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 800, color: 'hsl(var(--foreground))' }}>
                Meus Comunicados
              </h1>
              {totalNaoLidos > 0 && (
                <span style={{
                  background: '#2563eb',
                  color: '#ffffff',
                  fontSize: '.72rem',
                  fontWeight: 800,
                  padding: '2px 8px',
                  borderRadius: 12,
                }}>
                  {totalNaoLidos} novo(s)
                </span>
              )}
            </div>
            <p style={{ margin: '2px 0 0', fontSize: '.82rem', color: 'hsl(var(--muted-foreground))' }}>
              Caixa de entrada pessoal do operador ({user?.nome || 'Usuário'}) · Notificações, comunicados recebidos e avisos de segurança
            </p>
          </div>
        </div>

        {/* Ações de Topo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={loadData}
            disabled={loading}
            style={{
              padding: '8px 14px',
              borderRadius: 8,
              border: '1.5px solid #cbd5e1',
              background: '#ffffff',
              color: '#334155',
              fontSize: '.8rem',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              transition: 'all .15s ease',
            }}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Atualizar
          </button>

          {totalNaoLidos > 0 && (
            <button
              type="button"
              onClick={handleMarkAllAsRead}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: 'none',
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                color: '#ffffff',
                fontSize: '.8rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: '0 2px 6px rgba(37, 99, 235, 0.25)',
              }}
            >
              <CheckCircle2 size={14} />
              Marcar todos como lidos
            </button>
          )}
        </div>
      </div>

      {/* Card Principal com Barra de Filtros e Busca */}
      <div style={{
        background: 'hsl(var(--card))',
        border: '1px solid hsl(var(--border))',
        borderRadius: 14,
        padding: 16,
        boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}>
        {/* Linha de Filtros */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 10,
        }}>
          {/* Abas por Origem */}
          <div style={{
            display: 'flex',
            background: 'hsl(var(--muted))',
            padding: 3,
            borderRadius: 8,
            gap: 4,
            overflowX: 'auto',
            maxWidth: '100%',
            WebkitOverflowScrolling: 'touch',
          }}>
            <button
              type="button"
              onClick={() => setActiveTab('todos')}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                border: 'none',
                fontSize: '.78rem',
                fontWeight: activeTab === 'todos' ? 800 : 600,
                background: activeTab === 'todos' ? 'hsl(var(--card))' : 'transparent',
                color: activeTab === 'todos' ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))',
                cursor: 'pointer',
                boxShadow: activeTab === 'todos' ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
            >
              Todas ({unifiedList.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('comunicados')}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                border: 'none',
                fontSize: '.78rem',
                fontWeight: activeTab === 'comunicados' ? 800 : 600,
                background: activeTab === 'comunicados' ? 'hsl(var(--card))' : 'transparent',
                color: activeTab === 'comunicados' ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))',
                cursor: 'pointer',
                boxShadow: activeTab === 'comunicados' ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
            >
              <Megaphone size={13} />
              Comunicados & Avisos ({comunicados.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('seguranca')}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                border: 'none',
                fontSize: '.78rem',
                fontWeight: activeTab === 'seguranca' ? 800 : 600,
                background: activeTab === 'seguranca' ? 'hsl(var(--card))' : 'transparent',
                color: activeTab === 'seguranca' ? securityColor : 'hsl(var(--muted-foreground))',
                cursor: 'pointer',
                boxShadow: activeTab === 'seguranca' ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
            >
              <Shield size={13} style={{ color: activeTab === 'seguranca' ? securityColor : undefined }} />
              Segurança & Acessos ({totalSeguranca})
            </button>
          </div>

          {/* Filtro Status (Todos / Não Lidos / Lidos) + Busca */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, justifyContent: 'flex-end', minWidth: 260 }}>
            <div style={{ display: 'flex', background: 'hsl(var(--muted))', padding: 3, borderRadius: 8, gap: 2 }}>
              <button
                type="button"
                onClick={() => setFilterStatus('todos')}
                style={{
                  padding: '5px 10px',
                  borderRadius: 6,
                  border: 'none',
                  fontSize: '.72rem',
                  fontWeight: filterStatus === 'todos' ? 700 : 500,
                  background: filterStatus === 'todos' ? 'hsl(var(--card))' : 'transparent',
                  color: filterStatus === 'todos' ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))',
                  cursor: 'pointer',
                }}
              >
                Todos
              </button>
              <button
                type="button"
                onClick={() => setFilterStatus('nao_lidos')}
                style={{
                  padding: '5px 10px',
                  borderRadius: 6,
                  border: 'none',
                  fontSize: '.72rem',
                  fontWeight: filterStatus === 'nao_lidos' ? 700 : 500,
                  background: filterStatus === 'nao_lidos' ? 'hsl(var(--card))' : 'transparent',
                  color: filterStatus === 'nao_lidos' ? '#2563eb' : 'hsl(var(--muted-foreground))',
                  cursor: 'pointer',
                }}
              >
                Não Lidos
              </button>
              <button
                type="button"
                onClick={() => setFilterStatus('lidos')}
                style={{
                  padding: '5px 10px',
                  borderRadius: 6,
                  border: 'none',
                  fontSize: '.72rem',
                  fontWeight: filterStatus === 'lidos' ? 700 : 500,
                  background: filterStatus === 'lidos' ? 'hsl(var(--card))' : 'transparent',
                  color: filterStatus === 'lidos' ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))',
                  cursor: 'pointer',
                }}
              >
                Lidos
              </button>
            </div>

            {/* Input de Busca */}
            <div style={{ position: 'relative', width: 240 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por título, emissor..."
                style={{
                  width: '100%',
                  padding: '7px 12px 7px 30px',
                  borderRadius: 8,
                  border: '1.5px solid #cbd5e1',
                  background: '#ffffff',
                  fontSize: '.78rem',
                  color: '#0f172a',
                  outline: 'none',
                }}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#94a3b8',
                  }}
                >
                  <X size={13} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Lista de Comunicados & Alertas */}
        {filteredList.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 16px', color: '#64748b' }}>
            <div style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: '#f1f5f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 12px',
              color: '#94a3b8',
            }}>
              <Megaphone size={28} />
            </div>
            <strong style={{ fontSize: '.95rem', display: 'block', color: '#1e293b' }}>
              Nenhum comunicado correspondente
            </strong>
            <span style={{ fontSize: '.8rem', color: '#64748b', display: 'block', marginTop: 4 }}>
              {search ? 'Nenhum resultado encontrado para a busca.' : 'Você está em dia com todas as suas notificações.'}
            </span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {filteredList.map((item) => {
              const isEmailAlert = item.actionType === 'email_verification';
              const isPushAlert = item.actionType === 'push_activation';
              const isComunicado = item.origem === 'comunicado';
              const midiaUrl = getMidiaUrl(item);
              const sender = resolveSenderInfo(item);
              const target = resolveTargetInfo(item, user?.perfil);
              const relTime = formatRelativeTime(item.criadoEm);
              const absTime = formatDateTime(item.criadoEm);

              // ─── CARD 1: E-MAIL NÃO VERIFICADO ───
              if (isEmailAlert) {
                return (
                  <div
                    key={item.id}
                    style={{
                      padding: '16px 18px',
                      borderRadius: 12,
                      border: '1.5px solid rgba(239, 68, 68, 0.4)',
                      background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(220, 38, 38, 0.05) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 12,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 260, flex: 1 }}>
                      <div style={{
                        width: 42,
                        height: 42,
                        borderRadius: 10,
                        background: '#fee2e2',
                        color: '#ef4444',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <AlertTriangle size={22} />
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <strong style={{ fontSize: '.92rem', color: '#991b1b' }}>{item.titulo}</strong>
                          <span style={{ background: '#ef4444', color: '#fff', fontSize: '.62rem', fontWeight: 800, padding: '2px 8px', borderRadius: 6 }}>
                            AÇÃO NECESSÁRIA
                          </span>
                        </div>
                        <p style={{ margin: '3px 0 0', fontSize: '.8rem', color: '#7f1d1d', lineHeight: 1.4 }}>
                          {item.mensagem}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => window.dispatchEvent(new CustomEvent('ac:email:verify:open'))}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 8,
                        border: 'none',
                        background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                        color: '#ffffff',
                        fontSize: '.8rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
                        flexShrink: 0,
                      }}
                    >
                      <Mail size={14} />
                      Verificar E-mail Agora
                    </button>
                  </div>
                );
              }

              // ─── CARD 2: PUSH DESATIVADO ───
              if (isPushAlert) {
                return (
                  <div
                    key={item.id}
                    style={{
                      padding: '16px 18px',
                      borderRadius: 12,
                      border: '1.5px solid rgba(245, 158, 11, 0.4)',
                      background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(217, 119, 6, 0.05) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 12,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 260, flex: 1 }}>
                      <div style={{
                        width: 42,
                        height: 42,
                        borderRadius: 10,
                        background: '#fef3c7',
                        color: '#d97706',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <Megaphone size={22} />
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <strong style={{ fontSize: '.92rem', color: '#92400e' }}>{item.titulo}</strong>
                          <span style={{ background: '#f59e0b', color: '#fff', fontSize: '.62rem', fontWeight: 800, padding: '2px 8px', borderRadius: 6, flexShrink: 0 }}>
                            CONFIGURAÇÃO
                          </span>
                        </div>
                        <p style={{ margin: '3px 0 0', fontSize: '.8rem', color: '#78350f', lineHeight: 1.4 }}>
                          {item.mensagem}
                        </p>
                      </div>
                    </div>
                    {permissionStatus !== 'denied' && (
                      <button
                        type="button"
                        onClick={handleActivatePush}
                        disabled={notifLoading}
                        style={{
                          padding: '8px 16px',
                          borderRadius: 8,
                          border: 'none',
                          background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                          color: '#ffffff',
                          fontSize: '.8rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
                          flexShrink: 0,
                        }}
                      >
                        {notifLoading ? <Loader2 size={14} className="animate-spin" /> : <Megaphone size={14} />}
                        Ativar Notificações
                      </button>
                    )}
                  </div>
                );
              }

              // ─── CARD 3: DISPOSITIVO INATIVO (+30 DIAS) ───
              if (item.actionType === 'device_disconnect') {
                const dev = item.meta;
                const timeInfo = formatRelativeDeviceAccess(dev?.ultimoAcesso, false);
                return (
                  <div
                    key={item.id}
                    style={{
                      padding: '16px 18px',
                      borderRadius: 12,
                      border: '1.5px solid rgba(225, 29, 72, 0.4)',
                      background: 'linear-gradient(135deg, rgba(244, 63, 94, 0.08) 0%, rgba(225, 29, 72, 0.04) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 12,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 260, flex: 1 }}>
                      <div style={{
                        width: 42,
                        height: 42,
                        borderRadius: 10,
                        background: '#ffe4e6',
                        color: '#e11d48',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <AlertTriangle size={22} />
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <strong style={{ fontSize: '.92rem', color: '#9f1239' }}>{item.titulo}</strong>
                          <span style={{ background: '#e11d48', color: '#fff', fontSize: '.62rem', fontWeight: 800, padding: '2px 8px', borderRadius: 6 }}>
                            SEGURANÇA
                          </span>
                          <span style={{ background: '#fecdd3', color: '#881337', fontSize: '.62rem', fontWeight: 700, padding: '2px 6px', borderRadius: 6 }}>
                            {timeInfo.shortLabel}
                          </span>
                        </div>
                        <p style={{ margin: '3px 0 0', fontSize: '.8rem', color: '#881337', lineHeight: 1.4 }}>
                          {item.mensagem}
                        </p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={() => {
                          if (dev?.id) {
                            revokeUserDevice(dev.id);
                            toast.success(`Dispositivo "${dev.nome || dev.navegadorDetectado}" desconectado.`);
                          }
                        }}
                        style={{
                          padding: '8px 16px',
                          borderRadius: 8,
                          border: 'none',
                          background: 'linear-gradient(135deg, #e11d48, #be123c)',
                          color: '#ffffff',
                          fontSize: '.8rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          boxShadow: '0 2px 8px rgba(225, 29, 72, 0.3)',
                        }}
                      >
                        Desconectar Dispositivo
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const targetRoute =
                            user?.perfil === 'MUNICIPAL'
                              ? '/municipal/perfil'
                              : user?.perfil === 'PROFESSOR'
                                ? '/professor/perfil'
                                : user?.perfil === 'ALUNO'
                                  ? '/aluno/perfil'
                                  : user?.perfil === 'ESTADUAL'
                                    ? '/estadual/perfil'
                                    : user?.perfil === 'REDE'
                                      ? '/rede/perfil'
                                      : '/admin/perfil';
                          navigate(targetRoute);
                        }}
                        style={{
                          padding: '8px 14px',
                          borderRadius: 8,
                          border: '1px solid #fda4af',
                          background: '#fff',
                          color: '#9f1239',
                          fontSize: '.8rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Ver no Perfil
                      </button>
                    </div>
                  </div>
                );
              }

              // ─── CARD 4: COMUNICADO REGULAR / AVISO / SEGURANÇA ───
              const isUnread = !item.lido;

              return (
                <div
                  key={item.id}
                  onClick={() => {
                    setSelectedComunicado(item.meta || item);
                    handleMarkAsRead(item.id, item.origem);
                  }}
                  style={{
                    padding: '14px 16px',
                    borderRadius: 12,
                    border: isUnread ? '1.5px solid #3b82f6' : '1px solid #e2e8f0',
                    background: isUnread ? '#f0f7ff' : '#ffffff',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    cursor: 'pointer',
                    transition: 'all .15s ease',
                    boxShadow: isUnread
                      ? '0 4px 14px rgba(59, 130, 246, 0.09)'
                      : '0 1px 3px rgba(0, 0, 0, 0.02)',
                  }}
                >
                  {/* Top Bar: Emissor + Destinatário + Tipo & Status Novo */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
                      <span style={{
                        fontSize: '.66rem',
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: 6,
                        background: '#e0f2fe',
                        color: '#0369a1',
                        whiteSpace: 'nowrap',
                      }}>
                        {sender.label}
                      </span>

                      <span style={{
                        fontSize: '.66rem',
                        fontWeight: 600,
                        color: '#64748b',
                        whiteSpace: 'nowrap',
                      }}>
                        {target.label}
                      </span>

                      {target.isDirect && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 3,
                          padding: '2px 7px',
                          borderRadius: 6,
                          background: 'linear-gradient(135deg, #fef08a 0%, #fde047 100%)',
                          color: '#854d0e',
                          fontSize: '.62rem',
                          fontWeight: 800,
                          border: '1px solid #facc15',
                        }}>
                          <Sparkles size={10} /> Direcionado
                        </span>
                      )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      {isUnread && (
                        <span style={{
                          background: '#2563eb',
                          color: '#ffffff',
                          fontSize: '.58rem',
                          fontWeight: 800,
                          padding: '2px 6px',
                          borderRadius: 6,
                          boxShadow: '0 1px 4px rgba(37, 99, 235, 0.3)',
                        }}>
                          NOVO
                        </span>
                      )}

                      <span style={{
                        fontSize: '.66rem',
                        fontWeight: 700,
                        color: item.tipo === 'URGENTE' ? '#dc2626' : '#2563eb',
                        letterSpacing: '0.3px',
                        textTransform: 'uppercase',
                      }}>
                        {item.tipo || 'AVISO'}
                      </span>
                    </div>
                  </div>

                  {/* Corpo: Título + Snippet + Thumbnail ao lado */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: isUnread ? 800 : 600,
                        fontSize: '.9rem',
                        color: isUnread ? '#0f172a' : '#1e293b',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {item.titulo}
                      </div>

                      {item.mensagem && (
                        <p style={{
                          margin: '3px 0 0',
                          fontSize: '.78rem',
                          color: isUnread ? '#334155' : '#64748b',
                          lineHeight: 1.35,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}>
                          {item.mensagem}
                        </p>
                      )}
                    </div>

                    {midiaUrl && (
                      <img
                        src={midiaUrl}
                        alt=""
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 8,
                          objectFit: 'cover',
                          border: '1px solid #cbd5e1',
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </div>

                  {/* Rodapé: ⏱️ há Xm à esquerda | 📅 DD/MM às HH:mm à direita */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '.7rem',
                    color: '#64748b',
                    borderTop: isUnread ? '1px solid rgba(147, 197, 253, 0.4)' : '1px solid #f1f5f9',
                    paddingTop: 6,
                    marginTop: 2,
                  }}>
                    <span style={{ fontWeight: 700, color: '#2563eb', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Clock size={12} /> {relTime}
                    </span>

                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Calendar size={12} /> {absTime}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ─── MODAL OFICIAL DE VISUALIZAÇÃO DO COMUNICADO (LAYOUT DE ENVIO + NAVEGAÇÃO) ─── */}
      <ComunicadoVisualizacaoModal
        open={!!selectedComunicado}
        onClose={() => setSelectedComunicado(null)}
        comunicadoInicial={selectedComunicado}
        listaComunicados={comunicados}
        readIds={readComunicadoIds}
        onMarkAsRead={(id) => handleMarkAsRead(id, 'comunicado')}
        userPerfil={user?.perfil}
      />

      {/* ─── LIGHTBOX / FULLSCREEN IMAGE PREVIEW ─── */}
      {previewImageUrl && (
        <div
          onClick={() => setPreviewImageUrl(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100000,
            padding: 20,
            cursor: 'zoom-out',
          }}
        >
          <button
            type="button"
            onClick={() => setPreviewImageUrl(null)}
            style={{
              position: 'absolute',
              top: 20,
              right: 20,
              background: 'rgba(255, 255, 255, 0.2)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <X size={20} />
          </button>
          <img
            src={previewImageUrl}
            alt="Mídia ampliada"
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: 8,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            }}
          />
        </div>
      )}
      {user?.perfil === 'PROFESSOR' && (
        <MobileTabBar hasPendencias={false} unreadComunicados={totalNaoLidos} />
      )}
    </div>
  );
}

// Export compatível
export { MeusComunicadosPage as MeusAlertasPage };
